import type { ReactNode } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";
import { dateAxis, yearEndTicks } from "./chart-date";
import { formatCompactCurrency } from "../../../pages/PortfolioPerformance/lib/format";

const AXIS_COLOR = "var(--neutral-inactive)";

/**
 * Upper bound for a 0-based axis with Recharts' default 5 ticks (4 steps). Steps are 1, 2 or 5
 * × 10ⁿ, so every tick is exact at the one-decimal tick format (no 0.75 printed as "0.8").
 */
export function niceAxisMax(rawMax: number): number {
  const rawStep = rawMax / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rawStep) ?? rawStep;
  return step * 4;
}

/** Dollars always in millions to one decimal: $450.0M, $1,200.0M, -$25.5M. */
function formatMillions(value: number): string {
  const text = Math.abs(value / 1e6).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${value < 0 ? "-" : ""}$${text}M`;
}

/** One decimal, or two when the tick needs it — Recharts' auto ticks can land on 0.45 / 1.35. */
function formatTick(value: number, suffix: string, decimals?: number, currency?: boolean | "millions"): string {
  if (currency === "millions") return formatMillions(value);
  if (currency) return formatCompactCurrency(value);
  if (decimals !== undefined) return `${value.toFixed(decimals)}${suffix}`;
  const oneDecimal = Math.abs(value * 10 - Math.round(value * 10)) < 1e-9;
  return `${value.toFixed(oneDecimal ? 1 : 2)}${suffix}`;
}

export interface TrendSeries {
  dataKey: string;
  name: string;
  color: string;
  dashed?: boolean;
  /** Draw a dot on each point (e.g. a series that may hold a single point). */
  dots?: boolean;
  /** dataKey of a series this one continues (e.g. a dotted projection joined to the last
   *  realized point). The tooltip omits this series' entry wherever that series has a value, so
   *  the shared join point is listed once. */
  continues?: string;
  /** Keep this series out of the legend (e.g. a dotted continuation that shares its solid line's colour). */
  hideFromLegend?: boolean;
}

/** Renders a hovered point's value in the tooltip as something other than the number (e.g. a High / Medium / Low label). `label` is the
 *  hovered x value (the point's month), which lets the caller look the point up in its own data. */
export type TrendValueLabel = (entry: { value: number; dataKey?: string; name: string; label?: string }) => ReactNode;

function TrendTooltip({
  active,
  payload,
  label,
  suffix,
  decimals = 2,
  currency,
  continues,
  valueLabel,
  labelFormatter,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; dataKey?: string }>;
  label?: string;
  suffix: string;
  decimals?: number;
  currency?: boolean | "millions";
  continues?: Record<string, string>;
  valueLabel?: TrendValueLabel;
  labelFormatter?: (month: string) => string;
}) {
  if (!active || !payload?.length) return null;
  const present = new Set(payload.filter((e) => e.value != null).map((e) => e.dataKey));
  const entries = payload.filter(
    (entry) => !(entry.dataKey && continues?.[entry.dataKey] && present.has(continues[entry.dataKey])),
  );
  if (!entries.length) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">{label !== undefined && labelFormatter ? labelFormatter(label) : label}</p>
      <div className="mt-1 space-y-0.5">
        {entries.map((entry) => (
          <p key={entry.name} className="flex items-center gap-2 text-muted-foreground">
            <span className="size-2" style={{ backgroundColor: entry.color }} aria-hidden />
            {entry.name}
            <span className="ml-auto tabular-nums text-foreground">
              {valueLabel
                ? valueLabel({ value: entry.value, dataKey: entry.dataKey, name: entry.name, label })
                : currency === "millions"
                  ? formatMillions(entry.value)
                  : currency
                    ? formatCompactCurrency(entry.value)
                    : `${entry.value.toFixed(decimals)}${suffix}`}
            </span>
          </p>
        ))}
      </div>
    </ChartTooltipShell>
  );
}

export function TrendLineChart<T extends { month: string }>({
  data,
  series,
  suffix = "%",
  height = 300,
  referenceLine,
  bands,
  decimals,
  currency,
  valueLabel,
  yDomain,
  allXLabels,
  yearEnds,
}: {
  data: T[];
  series: TrendSeries[];
  suffix?: string;
  height?: number;
  referenceLine?: { value: number; label: string; color?: string };
  /** Watermark bands behind the lines: a faint tinted area per band with its name in the corner (e.g. Pass / Watch / ... on the
   *  APD trend). The axis keeps its own scale (so the line stays readable); only the bands that fall inside it are drawn, the last
   *  one cut off at the top of the chart. */
  bands?: Array<{ label: string; from: number; to: number; color: string }>;
  /** Fixed decimal places for ticks and tooltip (e.g. 0 for counts); omit for the default. */
  decimals?: number;
  /** Values are raw dollars: ticks and tooltip show $1.2K / $737.5M / $1.25B and ignore suffix/decimals. */
  currency?: boolean | "millions";
  /** Show each hovered value in the tooltip as this (a label, say) instead of the number. The axis keeps its numbers. */
  valueLabel?: TrendValueLabel;
  /** Fixed y axis [min, max] instead of one fitted to the data (e.g. [0, 100] for a score). The bands, if any, are cut at its top. */
  yDomain?: [number, number];
  /** Label every x entry (slanted so they fit) instead of letting Recharts drop the ones that would overlap, e.g. every quarter of a
   *  long quarterly series, not just every other one. */
  allXLabels?: boolean;
  /** Name one point per year on the x axis: its last one ("DEC-24" for a past year, the latest month for the year still running). */
  yearEnds?: boolean;
}) {
  // Dates on the axis and in the tooltip are month and year, "AUG-26", whatever style the point's `month` arrives in; anything that is not
  // a month (a year, "Current QTD") is left alone. See chart-date.ts. The points keep their own `month` as their key.
  const axis = dateAxis(data.map((row) => String(row.month)));
  const xTicks = (yearEnds ? yearEndTicks(data.map((row) => String(row.month))) : undefined) ?? axis.ticks;

  // With bands, the axis top is worked out here (the same rule as with a reference line) so each band can be cut to what is
  // visible and its label sits in view.
  const seriesMax = Math.max(0, ...data.flatMap((row) => series.map((s) => Number((row as Record<string, unknown>)[s.dataKey]) || 0)));
  const bandTop = bands?.length
    ? yDomain
      ? yDomain[1]
      : niceAxisMax(Math.max(seriesMax, (referenceLine?.value ?? 0) * 1.1))
    : undefined;
  const visibleBands = bandTop === undefined ? [] : (bands ?? []).filter((b) => b.from < bandTop);

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        {/* With year-end ticks the last one is the latest month, centred under the last point, so its right half needs room or it is cut off. */}
        <LineChart data={data} margin={{ top: 8, right: yearEnds ? 30 : 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
            tickFormatter={axis.tick}
            {...(xTicks ? { ticks: xTicks } : {})}
            {...(yearEnds && xTicks ? { interval: 0 } : {})}
            {...(allXLabels ? { interval: 0, angle: -45, textAnchor: "end" as const, height: 48 } : {})}
          />
          <YAxis
            tickFormatter={(value: number) => formatTick(value, suffix, decimals, currency)}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            width={56}
            // Keep a reference threshold on-chart even when the series sits entirely below it.
            {...(yDomain
              ? { domain: yDomain }
              : bandTop !== undefined
              ? { domain: [0, bandTop] }
              : referenceLine
              ? {
                  domain: [
                    0,
                    (dataMax: number) => niceAxisMax(Math.max(dataMax, referenceLine.value * 1.1)),
                  ],
                }
              : {})}
          />
          <Tooltip
            content={
              <TrendTooltip
                suffix={suffix}
                currency={currency}
                continues={Object.fromEntries(
                  series.flatMap((line) => (line.continues ? [[line.dataKey, line.continues]] : [])),
                )}
                {...(decimals !== undefined ? { decimals } : {})}
                {...(valueLabel ? { valueLabel } : {})}
                labelFormatter={axis.label}
              />
            }
          />
          <Legend
            wrapperStyle={{ fontSize: 11, color: "var(--muted-foreground)" }}
            iconType="plainline"
          />
          {visibleBands.map((b) => (
            <ReferenceArea
              key={b.label}
              y1={b.from}
              y2={Math.min(b.to, bandTop ?? b.to)}
              ifOverflow="hidden"
              fill={b.color}
              fillOpacity={0.1}
              stroke="none"
              label={{
                value: b.label.toUpperCase(),
                position: "insideTopRight",
                fill: b.color,
                fillOpacity: 0.7,
                fontSize: 10,
                fontWeight: 600,
              }}
            />
          ))}
          {visibleBands
            .filter((b) => b.from > 0)
            .map((b) => (
              <ReferenceLine key={`edge-${b.label}`} y={b.from} stroke={b.color} strokeOpacity={0.35} strokeDasharray="2 4" />
            ))}
          {referenceLine ? (
            <ReferenceLine
              y={referenceLine.value}
              stroke={referenceLine.color ?? "var(--decline)"}
              strokeDasharray="4 4"
              label={{
                value: referenceLine.label,
                position: "insideTopLeft",
                fill: referenceLine.color ?? "var(--decline)",
                fontSize: 11,
              }}
            />
          ) : null}
          {series.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              strokeWidth={1.75}
              strokeDasharray={line.dashed ? "4 3" : undefined}
              legendType={line.hideFromLegend ? "none" : undefined}
              dot={line.dots ? { r: 2.5 } : false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
